import RequestDataLink from './RequestDataLink';
import SearchSuggestions from "@/components/SearchSuggestions";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { MagnifyingGlass, Leaf, Database, UploadSimple, House, ShieldCheck, ChatCircleText } from "@phosphor-icons/react";
import { TEST_IDS } from "@/constants/testIds";
import { adminToken } from "@/lib/api";
import ContributionModal from "./ContributionModal";

const NavLink = ({ to, active, children, testId, icon: Icon }) => (
    <Link
        to={to}
        data-testid={testId}
        className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
            active
                ? "border-[#6B46C1] text-[#09090B]"
                : "border-transparent text-neutral-500 hover:text-[#09090B]"
        }`}
    >
        {Icon ? <Icon size={16} weight="duotone" /> : null}
        {children}
    </Link>
);

export default function Layout({ children }) {
    const loc = useLocation();
    const navigate = useNavigate();
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [q, setQ] = useState("");
    const [contributeOpen, setContributeOpen] = useState(false);

    const submitSearch = (e) => {
        e.preventDefault();
        setSuggestionsOpen(false);
        if (q.trim()) navigate(`/explore?q=${encodeURIComponent(q.trim())}`);
    };

    return (
        <div className="sdx-layout min-h-screen bg-[#FCFCFB] text-[#09090B] flex flex-col">
            <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
                <div className="sdx-header-inner max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between gap-4">
                    <Link
                        to="/"
                        data-testid={TEST_IDS.themeBrand}
                        className="flex items-center gap-3 group"
                    >
                        <span className="w-9 h-9 bg-[#6B46C1] flex items-center justify-center rounded-sm">
                            <Leaf size={20} color="#FFD60A" weight="fill" />
                        </span>
                        <div className="leading-tight">
                            <div className="font-serif text-xl font-medium tracking-tight">SustainDex</div>
                            <div className="label-caps text-[9px] -mt-0.5">
                                Sustainability Data Exchange
                            </div>
                        </div>
                    </Link>

                    <nav className="sdx-navigation flex items-center gap-1" aria-label="Main navigation">
                        <NavLink to="/" active={loc.pathname === "/"} testId={TEST_IDS.navHome} icon={House}>
                            Dashboard
                        </NavLink>
                        <NavLink
                            to="/explore"
                            active={loc.pathname.startsWith("/explore")}
                            testId={TEST_IDS.navExplore}
                            icon={Database}
                        >
                            Explore
                        </NavLink>
                        <NavLink
                            to={adminToken.get() ? "/admin" : "/admin/login"}
                            active={loc.pathname.startsWith("/admin")}
                            testId={TEST_IDS.adminNavLink}
                            icon={ShieldCheck}
                        >
                            Admin
                        </NavLink>
                    </nav>

                    <form onSubmit={submitSearch} className="sdx-nav-search flex items-center gap-2" onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setSuggestionsOpen(false); }} onKeyDown={e => { if (e.key === "Escape") setSuggestionsOpen(false); }}>
                        <div className="relative">
                            <MagnifyingGlass
                                size={16}
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400"
                            />
                            <input
                                data-testid={TEST_IDS.navSearchInput}
                                value={q}
                                onChange={(e) => { setQ(e.target.value); setSuggestionsOpen(true); }}
                                onFocus={() => setSuggestionsOpen(true)}
                                aria-label="Search the sustainability directory"
                                autoComplete="off"
                                placeholder="Search materials, processes, parameters…"
                                className="pl-8 pr-3 py-2 text-sm border border-neutral-200 rounded-sm w-64 focus:outline-none focus:border-[#6B46C1] bg-[#FCFCFB]"
                            />
                            {suggestionsOpen && <SearchSuggestions query={q} onSelect={result => {
                                setSuggestionsOpen(false);
                                setQ(result.name);
                                navigate(result.href || `/explore?q=${encodeURIComponent(result.name)}`);
                            }} />}
                        </div>
                        <button
                            data-testid={TEST_IDS.navSearchButton}
                            type="submit"
                            className="btn-primary"
                        >
                            Search
                        </button>
                    </form>
                </div>
            </header>

            <main className="flex-1">{children}</main>

            <div className="sdx-floating-actions" aria-label="Data actions">
                <button type="button" onClick={() => setContributeOpen(true)} className="sdx-floating-contribute" aria-label="Open contribution form">
                    <UploadSimple size={18} weight="bold" /> Contribute data
                </button>
                <RequestDataLink className="sdx-floating-request">
                    <ChatCircleText size={18} weight="bold" /> Request data
                </RequestDataLink>
            </div>

            {contributeOpen && <ContributionModal onClose={() => setContributeOpen(false)}/> }

            <footer className="border-t border-neutral-200 py-6 mt-10 bg-white">
                <div className="max-w-[1600px] mx-auto px-6 flex items-center justify-between text-xs text-neutral-500 flex-wrap gap-3">
                    <div>
                        © {new Date().getFullYear()} SustainDex · Sustainability Data Exchange
                    </div>
                    <div className="flex items-center gap-4"><RequestDataLink className="text-violet-700">Request data</RequestDataLink><button onClick={()=>setContributeOpen(true)} className="text-violet-700">Contribute data</button>
                        <span className="label-caps">v0.2 · Open Data</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
